// Story 6.11 (ADO-1165) — client-side RBAC mirror for the Admin User Management
// screen (roles, account types, the admin gate + form option lists).
//
// ┌─ SOURCE OF TRUTH ───────────────────────────────────────────────────────────┐
// │ The CANONICAL role / account-type lists live in `@shoplink/tenant-context`   │
// │ (`src/rbac/role.ts` ROLES, `account-type.ts` ACCOUNT_TYPES, `capability.ts`  │
// │ ROLE_CAPABILITIES — Story 4.7/#817). We would prefer to import them so this  │
// │ file cannot drift, but that barrel is NOT bundler-safe for the web build:    │
// │ `@shoplink/tenant-context/src/index.ts` re-exports NestJS `@Module()` /      │
// │ `@Injectable()` singletons, `reflect-metadata`, Prisma and Fastify, and the  │
// │ package exposes NO deep-import subpath (`package.json` `exports` = `.` only;  │
// │ `tsconfig.base.json` aliases only the barrel). Pulling it into `apps/web`    │
// │ (`type:app`) would drag the whole server stack into the SPA bundle.          │
// │                                                                              │
// │ So per Story 6.11 Dev Notes ("Admin-role gate — source of truth vs. bundler  │
// │ safety") this is the sanctioned FALLBACK: a small local mirror citing the    │
// │ source. FOLLOW-UP (OQ / DS-boundary): expose a browser-safe `rbac` subpath   │
// │ from `@shoplink/tenant-context` (or a shared `@shoplink/rbac` leaf lib) so    │
// │ the SPA can import ROLES/ACCOUNT_TYPES directly and delete this mirror.       │
// └──────────────────────────────────────────────────────────────────────────────┘
//
// NON-AUTHORITATIVE (NFR18): everything here is DISPLAY / ROUTING only. The server
// (Story 6.12 RolesGuard / @PersonaRestricted) re-checks every mutation — the SPA
// never trusts `session.role`. See `useSessionStore` for the same contract.

import type { SelectOption } from '../design-system';

/** Canonical role codes — mirror of `@shoplink/tenant-context` ROLES (role.ts). */
export const ROLE_CODES = [
  'SystemAdministrator',
  'Gatherer',
  'Manager',
  'StoreUser',
  'StoreManager',
  'SalesRep',
  'SupplierAdmin',
  'SupplierRep',
  'RedAlertAdmin',
  'PhotosImageryAdmin',
  'WowAdmin',
  'StoreManagerAdmin',
  'SupplierAdminManager',
  'StoreGroupManager',
] as const;

/** Canonical account-type codes — mirror of ACCOUNT_TYPES (account-type.ts). */
export const ACCOUNT_TYPE_CODES = ['LSN', 'Foodservice', 'LicenceTrade'] as const;

/**
 * Roles permitted to manage users. Mirrors `ADMIN_ROLES` from
 * `@shoplink/tenant-context`; the server remains authoritative.
 */
export const ADMIN_ROLES = [
  'SystemAdministrator',
  'StoreManagerAdmin',
  'SupplierAdminManager',
] as const;

/**
 * The client-side half of AC5 (Story 6.11). DISPLAY / ROUTING ONLY — the server
 * re-checks every action (NFR18). Never gate an authorisation decision on this.
 */
export function isAdminRole(role: string | null | undefined): boolean {
  return role != null && (ADMIN_ROLES as readonly string[]).includes(role);
}

/** Sensitive admin surfaces remain SystemAdministrator-only in R1. */
export function isSystemAdministrator(role: string | null | undefined): boolean {
  return role === 'SystemAdministrator';
}

/**
 * Story 9.3 (ADO-1081), D-5 — the supplier personas. Mirror of `SUPPLIER_ROLES` from
 * `@shoplink/tenant-context` (rbac/role.ts; same bundler-safety rationale as ROLE_CODES
 * above). DISPLAY ONLY (NFR18): this hides the "Track order" control for supplier
 * users, exactly as SL1.0's `SupplierUserID` gate did. The tracking endpoint re-checks
 * the same set server-side and is authoritative.
 */
export const SUPPLIER_ROLES = ['SupplierAdmin', 'SupplierRep', 'SupplierAdminManager'] as const;

/** True when `role` is a supplier persona (the 2.0 form of SL1.0's `SupplierUserID`). DISPLAY ONLY. */
export function isSupplierRole(role: string | null | undefined): boolean {
  return role != null && (SUPPLIER_ROLES as readonly string[]).includes(role);
}

/**
 * Roles that require a depot assignment — Story 6.16 (ADO-1195, OQ-6.11a).
 * Mirror of `@shoplink/tenant-context` DEPOT_REQUIRED_ROLES (rbac/depot-policy.ts
 * — same bundler-safety rationale as ROLE_CODES above; working decision
 * `StoreManager`, PO ratification flagged OQ-ADR-020b). The server gate is
 * authoritative (422 `depot-required`); this mirror only drives the form's
 * inline validation so the rejected submit never leaves the modal.
 */
export const DEPOT_REQUIRED_ROLES: readonly string[] = ['StoreManager'];

/** True when `role` requires a depot before it can be assigned (OQ-6.11a). */
export function isDepotRequiredRole(role: string | null | undefined): boolean {
  return role != null && DEPOT_REQUIRED_ROLES.includes(role);
}

/**
 * The roles an acting admin may ASSIGN to another user (AC5 — never expose an
 * action the API would reject). Only `SystemAdministrator` may confer
 * `SystemAdministrator`; lower user-management admins are offered the remaining
 * R1-compatible roles. DISPLAY ONLY — the API is authoritative.
 */
export const R1_ASSIGNABLE_ROLE_CODES = [
  'SystemAdministrator',
  'StoreManagerAdmin',
  'StoreUser',
  'StoreManager',
  'SupplierAdmin',
  'SalesRep',
] as const;

export function assignableRoleCodes(actingRole: string | null | undefined): readonly string[] {
  return actingRole === 'SystemAdministrator'
    ? R1_ASSIGNABLE_ROLE_CODES
    : R1_ASSIGNABLE_ROLE_CODES.filter((code) => code !== 'SystemAdministrator');
}

/** Human-readable labels for the role Select (display strings per docs/identity/rbac.md). */
const ROLE_LABELS: Record<(typeof ROLE_CODES)[number], string> = {
  SystemAdministrator: 'System Administrator',
  Gatherer: 'Gatherer',
  Manager: 'Manager',
  StoreUser: 'Store User',
  StoreManager: 'Store Manager',
  SalesRep: 'Sales Rep',
  SupplierAdmin: 'Supplier Admin',
  SupplierRep: 'Supplier Rep',
  RedAlertAdmin: 'Red-Alert Admin',
  PhotosImageryAdmin: 'Photos / Imagery Admin',
  WowAdmin: 'WOW Admin',
  StoreManagerAdmin: 'Store-Manager Admin',
  SupplierAdminManager: 'Supplier Admin (manager)',
  StoreGroupManager: 'Store Group Manager',
};

const ACCOUNT_TYPE_LABELS: Record<(typeof ACCOUNT_TYPE_CODES)[number], string> = {
  LSN: 'LSN',
  Foodservice: 'Foodservice',
  // `LicenceTrade` is camelCase code-side; the PRD display string carries the space.
  LicenceTrade: 'Licence Trade',
};

/** Options for the role `Select` in the Create / Edit / Assign-role forms. */
export const ROLE_OPTIONS: SelectOption[] = ROLE_CODES.map((value) => ({
  value,
  label: ROLE_LABELS[value],
}));

/** Options for the account-type `Select`. */
export const ACCOUNT_TYPE_OPTIONS: SelectOption[] = ACCOUNT_TYPE_CODES.map((value) => ({
  value,
  label: ACCOUNT_TYPE_LABELS[value],
}));

/** Best-effort display label for a role code (falls back to the raw code). */
export function roleLabel(role: string): string {
  return (ROLE_LABELS as Record<string, string>)[role] ?? role;
}

/** Best-effort display label for an account-type code. */
export function accountTypeLabel(accountType: string): string {
  return (ACCOUNT_TYPE_LABELS as Record<string, string>)[accountType] ?? accountType;
}

/**
 * Who sees the RBP core marks and filter: the store-user roles and a sales rep acting for a store.
 * Mirror of `RBP_BROWSE_SIDE_ROLES` (`@shoplink/rbp`, `contracts/rbp-roles.ts`); DISPLAY ONLY, the
 * server's core-marks handler and search's RBP codes decide.
 */
export const RBP_BROWSE_SIDE_ROLES: readonly string[] = [
  'Gatherer',
  'Manager',
  'StoreUser',
  'SalesRep',
];
